/** Minimum password length (pen test #12 / WorkOS alignment). */
export const MIN_PASSWORD_LENGTH = 10;

const COMMON_WEAK_PASSWORDS = new Set([
  "password",
  "password1",
  "password12",
  "password123",
  "test123",
  "test1234",
  "qwerty123",
  "1234567890",
  "123456789",
  "letmein",
  "welcome1",
  "admin123",
  "changeme",
]);

export interface PasswordStrength {
  score: number;
  label: "Weak" | "Fair" | "Good" | "Strong";
  checks: { label: string; met: boolean }[];
  isCommonWeak: boolean;
}

export function isCommonWeakPassword(password: string): boolean {
  return COMMON_WEAK_PASSWORDS.has(password.trim().toLowerCase());
}

/** Mirrors backend validate_password_strength for registration UX. */
export function calculatePasswordStrength(password: string): PasswordStrength {
  const isCommonWeak = isCommonWeakPassword(password);
  const checks = [
    { label: `At least ${MIN_PASSWORD_LENGTH} characters`, met: password.length >= MIN_PASSWORD_LENGTH },
    { label: "Contains uppercase letter", met: /[A-Z]/.test(password) },
    { label: "Contains lowercase letter", met: /[a-z]/.test(password) },
    { label: "Contains number", met: /[0-9]/.test(password) },
    { label: "Contains special character", met: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];

  const score = checks.filter((c) => c.met).length;

  let label: PasswordStrength["label"];
  if (isCommonWeak || score <= 2) {
    label = "Weak";
  } else if (score === 3) {
    label = "Fair";
  } else if (score === 4) {
    label = "Good";
  } else {
    label = "Strong";
  }

  return { score, label, checks, isCommonWeak };
}

export const PASSWORD_POLICY_ERROR =
  "Password must be at least 10 characters, not commonly used, and meet at least 3 strength checks (uppercase, lowercase, number, special character).";

export function isPasswordAcceptable(password: string): boolean {
  const strength = calculatePasswordStrength(password);
  return !strength.isCommonWeak && password.length >= MIN_PASSWORD_LENGTH && strength.score >= 3;
}
